/**
 * Getting a binder out of the app and back in again.
 *
 * Two directions, because they answer different questions. A file carries the
 * whole layout — which card is in which hole, on what size page — and is what
 * you send someone or keep as a backup. A plain list carries only the cards,
 * in order, and is what you paste into the stack box to find out what the
 * binder is worth.
 *
 * All of it is turning one shape into another, so it lives apart from the
 * screen and can be checked on its own.
 */

export const FORMAT = "pokepricer-binder";
export const VERSION = 1;

const MAX_PAGES = 60;
const MAX_SIDE = 6;

/** The binder as a file: slots listed rather than sparse, which reads far
 *  better to a person opening the file to see what is in it. */
export function toFile(binder) {
  const cards = Object.entries(binder.slots || {})
    .map(([slot, c]) => ({ slot: Number(slot), ...c }))
    .filter((c) => Number.isFinite(c.slot) && c.name)
    .sort((a, b) => a.slot - b.slot);

  return {
    format: FORMAT,
    version: VERSION,
    name: binder.name || "Binder",
    cols: binder.cols,
    rows: binder.rows,
    pages: binder.pages,
    cards,
  };
}

const clampSide = (n, fallback) => {
  const v = Math.round(Number(n));
  return Number.isFinite(v) && v >= 1 && v <= MAX_SIDE ? v : fallback;
};

/**
 * A file back into a binder.
 *
 * @returns {{ binder, cards }} on success, or {{ error }} — never a partly
 *   applied import. Half a layout is worse than none: you cannot tell what is
 *   missing by looking at it.
 */
export function fromFile(raw) {
  let data = raw;
  if (typeof raw === "string") {
    try {
      data = JSON.parse(raw);
    } catch {
      return { error: "That is not a binder file — it isn't even JSON." };
    }
  }
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return { error: "That file has nothing in it that looks like a binder." };
  }
  /* The marker is checked but a missing one is not fatal — a file someone has
     edited by hand, or built themselves, should still come in if its contents
     make sense. */
  if (data.format && data.format !== FORMAT) {
    return { error: `That is a ${data.format} file, not a binder.` };
  }
  if (!Array.isArray(data.cards)) {
    return { error: "That binder has no cards list in it." };
  }

  const cols = clampSide(data.cols, 3);
  const rows = clampSide(data.rows, 3);
  const pages = Math.max(1, Math.min(MAX_PAGES, Math.round(Number(data.pages)) || 1));

  const slots = {};
  let kept = 0;
  for (const c of data.cards) {
    const slot = Math.round(Number(c?.slot));
    if (!Number.isFinite(slot) || slot < 0 || !c?.name) continue;
    slots[slot] = {
      id: c.id ?? null,
      name: String(c.name),
      setName: c.setName ?? null,
      number: c.number ?? null,
      image: c.image ?? null,
      ...(Number.isFinite(Number(c.price)) ? { price: Number(c.price) } : {}),
    };
    kept += 1;
  }

  return {
    binder: {
      name: typeof data.name === "string" && data.name.trim() ? data.name : "Binder",
      cols, rows, pages, slots,
    },
    cards: kept,
    skipped: data.cards.length - kept,
  };
}

/**
 * The cards as a list, in the order they sit in the binder.
 *
 * Deliberately the format the stack box already reads — name then number — so
 * a binder can be priced by pasting it in, rather than needing a second way to
 * do the same thing.
 */
export function toText(binder) {
  return Object.entries(binder.slots || {})
    .map(([slot, c]) => ({ slot: Number(slot), c }))
    .filter(({ slot, c }) => Number.isFinite(slot) && c?.name)
    .sort((a, b) => a.slot - b.slot)
    .map(({ c }) => (c.number ? `${c.name} ${c.number}` : c.name))
    .join("\n");
}

/** A filename that says what it is and which binder it came from. */
export function fileName(binder) {
  const stem = String(binder?.name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
  // A binder with no name gets "binder.json", not "binder.binder.json".
  return stem ? `${stem}.binder.json` : "binder.json";
}
